import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";

import {
  apiClient,
  type DepartmentSummary,
  type ManagedUser,
  type ManagedUserFilters,
  type UserRole,
} from "../api/client";
import { useAuth } from "../app/AuthContext";
import { errorMessage } from "./admin/errors";
import { AddUserDialog } from "./users/AddUserDialog";
import { UserDetailDrawer } from "./users/UserDetailDrawer";

export function UserManagementPage() {
  const { session } = useAuth();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [filters, setFilters] = useState<ManagedUserFilters>({});
  const [draft, setDraft] = useState<ManagedUserFilters>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ManagedUser | null>(null);

  const loadUsers = useCallback(async (selected: ManagedUserFilters) => {
    setLoading(true);
    setError("");
    try {
      setUsers(await apiClient.listUsers(selected));
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUsers(filters);
  }, [filters, loadUsers]);

  useEffect(() => {
    const request =
      session?.role === "owner"
        ? apiClient.listDepartments().then((items) => items)
        : apiClient.listUserDepartments();
    request.then(setDepartments).catch((caught: unknown) => setError(errorMessage(caught)));
  }, [session?.role]);

  const departmentNames = useMemo(
    () =>
      new Map(
        departments.map((department) => [
          department.id,
          `${department.name} · ${department.code}${department.is_active === false ? " · 已停用" : ""}`,
        ]),
      ),
    [departments],
  );
  const activeDepartments = useMemo(
    () => departments.filter((department) => department.is_active !== false),
    [departments],
  );

  if (session?.role !== "manager" && session?.role !== "owner") {
    return (
      <section className="empty-page">
        <span className="eyebrow">权限不足</span>
        <h1>用户管理</h1>
        <p>只有完成 MFA 的 manager 和 owner 可以查看受范围控制的用户目录。</p>
      </section>
    );
  }

  function submitFilters(event: FormEvent) {
    event.preventDefault();
    setFilters({
      ...draft,
      search: draft.search?.trim() || undefined,
      departmentId: draft.departmentId || undefined,
    });
  }

  return (
    <div className="page-stack user-management-page">
      <header className="flow-header">
        <div>
          <span className="eyebrow">范围化账户目录</span>
          <h1>用户管理</h1>
          <p>目录在服务器端排除当前账户，并按角色、活动部门授权和筛选条件限制结果。</p>
        </div>
        <span className="phase-badge">{session.role}</span>
      </header>

      <section className="flow-panel user-directory-panel">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">可管理账户</span>
            <h2>用户目录</h2>
          </div>
          <button
            className="primary-link"
            onClick={() => {
              setSelectedUser(null);
              setCreateOpen(true);
            }}
            type="button"
          >
            添加用户
          </button>
        </div>

        <form className="user-directory-filters" onSubmit={submitFilters}>
          <label>
            搜索
            <input
              onChange={(event) => setDraft({ ...draft, search: event.target.value })}
              placeholder="用户名或显示名称"
              type="search"
              value={draft.search ?? ""}
            />
          </label>
          <label>
            角色
            <select
              onChange={(event) =>
                setDraft({
                  ...draft,
                  role: (event.target.value || undefined) as UserRole | undefined,
                })
              }
              value={draft.role ?? ""}
            >
              <option value="">全部角色</option>
              <option value="user">user</option>
              <option value="manager">manager</option>
              {session.role === "owner" && <option value="owner">owner</option>}
            </select>
          </label>
          <label>
            部门
            <select
              onChange={(event) =>
                setDraft({ ...draft, departmentId: event.target.value || undefined })
              }
              value={draft.departmentId ?? ""}
            >
              <option value="">全部部门</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name} · {department.code}
                </option>
              ))}
            </select>
          </label>
          <label>
            状态
            <select
              onChange={(event) =>
                setDraft({
                  ...draft,
                  state: (event.target.value || undefined) as
                    ManagedUser["account_state"] | undefined,
                })
              }
              value={draft.state ?? ""}
            >
              <option value="">全部状态</option>
              <option value="active">已启用</option>
              <option value="disabled">已停用</option>
              <option value="archived">已归档</option>
            </select>
          </label>
          <div className="user-filter-actions">
            <button type="submit">应用筛选</button>
            <button
              className="secondary-button"
              onClick={() => {
                setDraft({});
                setFilters({});
              }}
              type="button"
            >
              清除
            </button>
          </div>
        </form>

        {error && (
          <p className="alert error" role="alert">
            {error}
          </p>
        )}
        <UserDirectoryTable
          departmentNames={departmentNames}
          loading={loading}
          onSelect={(user) => {
            setCreateOpen(false);
            setSelectedUser(user);
          }}
          users={users}
        />
      </section>

      {createOpen && (
        <AddUserDialog
          actorRole={session.role}
          departments={activeDepartments}
          onClose={() => setCreateOpen(false)}
          onCreated={() => void loadUsers(filters)}
        />
      )}
      {selectedUser && (
        <UserDetailDrawer
          key={selectedUser.id}
          actorRole={session.role}
          departments={departments}
          onClose={() => setSelectedUser(null)}
          onUpdated={(updated) => {
            setSelectedUser(updated);
            setUsers((current) => current.map((user) => (user.id === updated.id ? updated : user)));
            void loadUsers(filters);
          }}
          user={selectedUser}
        />
      )}
    </div>
  );
}

function UserDirectoryTable({
  users,
  loading,
  departmentNames,
  onSelect,
}: {
  users: ManagedUser[];
  loading: boolean;
  departmentNames: Map<string, string>;
  onSelect: (user: ManagedUser) => void;
}) {
  if (loading) return <p className="panel-copy">正在读取用户目录…</p>;
  if (!users.length) return <p className="panel-copy">当前范围和筛选条件下没有账户。</p>;
  return (
    <div className="user-directory-table-wrap">
      <table className="user-directory-table">
        <thead>
          <tr>
            <th>用户</th>
            <th>主部门</th>
            <th>角色</th>
            <th>账户状态</th>
            <th>安全设置</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td>
                <strong>{user.display_name}</strong>
                <span>{user.username}</span>
              </td>
              <td>
                {user.primary_department_id
                  ? (departmentNames.get(user.primary_department_id) ?? user.primary_department_id)
                  : "未设置"}
              </td>
              <td>{user.role}</td>
              <td>
                <span className={`account-state ${user.account_state}`}>
                  {accountStateLabel(user.account_state)}
                </span>
              </td>
              <td>{user.must_change_password ? "待替换临时密码" : "密码已设置"}</td>
              <td>
                <button className="text-button" onClick={() => onSelect(user)} type="button">
                  查看 {user.display_name}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function accountStateLabel(state: ManagedUser["account_state"]): string {
  return { active: "已启用", disabled: "已停用", archived: "已归档" }[state];
}
